
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SpmTableApi from '@/components/table/SpmTableApi.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import ImportModal from '@/components/modals/ImportModal.vue';
import CampaignsBulkBaseSelectorV2 from '@/components/automated-scenarios/CampaignsBulkBaseSelectorV2.vue';
import {
  CampaignsBulkStatusEnum,
  SpmTableAction,
  SpmTableColumns,
  SpmTableFilter,
  SpmTableSort,
  TypeCampaignEnum,
} from '@/types';
import {
  CampaignsBulkDuplicateInputItem,
  OperatorType,
} from '@/types/generated-types/graphql';
import {
  AutomatedScenarioState as stateAutomatedScenario,
  setRefreshScenariosList,
} from '@/composables/AutomatedScenarios';
import {
  activateCampaign,
  setAnalyticsMode,
} from '@/composables/AutomatedScenarios';
import { findPermission, UserState, hasAccessToFeatures } from '@/composables/User';
import CampaignPerformanceMetrics from '@/components/automated-scenarios/CampaignPerformanceMetrics.vue';
import type { CampaignMetricDisplay } from '@/components/automated-scenarios/CampaignPerformanceMetrics.vue';
import { store } from '@/store';
import { duplicateBulkCampaign, getBulkCampaignStatus } from '@/composables/workflows/CampaignBulk';
import { showToastError, showToastSuccess } from '@/helpers';
import { getNotConfiguredServices } from '@/composables/shop/MyShopParameters';
import { registerExportRequest } from '@/composables/useExport';
import type { MenuItem } from 'primevue/menuitem';
import {
  getChannelCardStyleVars,
  getChannelIcon,
  getChannelTagStyleVars,
  getStatusIcon,
  getStatusStyleVars,
} from '@/views/automated-scenarios/utils/styleTokens';

const percentageFormatter = new Intl.NumberFormat(undefined, {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});

const amountFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export default defineComponent({
  name: 'BulkCampaignsList',
  components: {
    SpmButton,
    SpmTableApi,
    ImportModal,
    CampaignsBulkBaseSelectorV2,
    CampaignPerformanceMetrics,
  },
  setup() {
    const { t, locale } = useI18n();
    const idShop = ref<number>(UserState.activeShop?.id ?? 0);
    type SpmTableApiInstance = InstanceType<typeof SpmTableApi> & {
      unselectAllRows?: () => void;
      toggleDialog?: (...args: any[]) => void;
      reloadData?: () => void;
    };

    const tableRef = ref<SpmTableApiInstance | null>(null);
    const openWorkflowSelector = ref(false);
    const isImportModalVisible = ref(false);
    const isProcessingAction = ref(false);
    const dataExportPermission = hasAccessToFeatures('data.campaign.export');

    const registerExportRequestCommand = async (idsTobeExported: number[]) => {
      try {
        await registerExportRequest(
          idsTobeExported,
          idShop.value,
          UserState.user.id,
          UserState.user.userType,
          'campaign',
        );
        await showToastSuccess(t('campaigns.bulk.export.exportSuccess'));
        tableRef.value?.unselectAllRows?.();
      } catch (error) {
        await showToastError(t('campaigns.bulk.export.exportFailure'));
      }
    };

    const hiddenStatuses: CampaignsBulkStatusEnum[] = [
      CampaignsBulkStatusEnum.ARCHIVED,
      CampaignsBulkStatusEnum.ARCHIVED_BY_USER,
      CampaignsBulkStatusEnum.DELETED,
    ];

    const statusOptions = computed(() =>
      (Object.values(CampaignsBulkStatusEnum) as CampaignsBulkStatusEnum[])
        .filter((status) => !hiddenStatuses.includes(status))
        .map((status) => ({
          value: status,
          label: t(`campaigns.bulk.list.status.${status}`),
        })),
    );

    const persistentFilters = computed<SpmTableFilter[]>(() =>
      hiddenStatuses.map((status) => ({
        field: 'status',
        value: status,
        operator: OperatorType.NotEquals,
      })),
    );

    const openCampaignPreview = async (campaign: any) => {
      if (!campaign?.id || isProcessingAction.value) {
        return;
      }

      try {
        isProcessingAction.value = true;
        store.commit('general/showTheSpinner');
        const currentStatus = await getBulkCampaignStatus(campaign.id);
        if (!currentStatus || currentStatus !== campaign.status) {
          await showToastError(t('errorMessages.GENERIC_ERROR'));
          setRefreshScenariosList(true);
          return;
        }

        await activateCampaign(campaign.id, TypeCampaignEnum.BULK, campaign.date_send ?? null);
        await setAnalyticsMode(true);
      } catch (error: any) {
        await showToastError(error?.message ?? t('errorMessages.GENERIC_ERROR'));
      } finally {
        store.commit('general/hideTheSpinner');
        isProcessingAction.value = false;
      }
    };

    const openCampaignEditor = async (campaign: any) => {
      if (!campaign?.id || isProcessingAction.value) {
        return;
      }

      try {
        isProcessingAction.value = true;
        store.commit('general/showTheSpinner');
        const currentStatus = await getBulkCampaignStatus(campaign.id);
        if (!currentStatus || currentStatus !== campaign.status) {
          await showToastError(t('errorMessages.GENERIC_ERROR'));
          setRefreshScenariosList(true);
          return;
        }

        await activateCampaign(campaign.id, TypeCampaignEnum.BULK);
      } catch (error: any) {
        await showToastError(error?.message ?? t('errorMessages.GENERIC_ERROR'));
      } finally {
        store.commit('general/hideTheSpinner');
        isProcessingAction.value = false;
      }
    };

    const groupActions = computed<MenuItem[]>(() => [
      {
        code: 'export',
        label: t('campaigns.bulk.list.groupedActions.export.menuItem'),
        command: () => {
          const execute = () => {
            if (!tableRef.value) {
              return;
            }

            tableRef.value?.toggleDialog?.(
              '',
              t('campaigns.bulk.list.groupedActions.export.text'),
              t('campaigns.bulk.list.groupedActions.export.formTitle'),
              '',
              t('yes'),
              t('no'),
              'custom',
              null,
              '',
              registerExportRequestCommand,
            );
          };
          execute();
        },
      },
    ]);

    const duplicateCampaign = async (campaign: any) => {
      if (!campaign?.id || isProcessingAction.value) {
        return;
      }

      try {
        isProcessingAction.value = true;
        const notConfiguredServices = await getNotConfiguredServices(idShop.value, ['duplicateScenarioCampaign']);
        if (notConfiguredServices.length) {
          store.commit('general/setNotConfiguredServices', notConfiguredServices);
          store.commit('general/setIsServicesParametersModalVisible', true);
          isProcessingAction.value = false;
          return;
        }

        const duplicateInput: CampaignsBulkDuplicateInputItem = {
          id_campaign_bulk: campaign.id,
          id_shop_destination: idShop.value,
          base_campaign: 0,
          name: `${campaign.name} (${t('campaigns.bulk.list.duplicateSuffix')})`,
        };

        const result = await duplicateBulkCampaign(duplicateInput);
        if (result.success) {
          await showToastSuccess(t('campaigns.bulk.list.actions.duplicate.success'));
          setRefreshScenariosList(true);
          return;
        }
        await showToastError(t('campaigns.bulk.list.actions.duplicate.error'));
      } catch (error: any) {
        await showToastError(error?.message ?? t('campaigns.bulk.list.actions.duplicate.error'));
      } finally {
        isProcessingAction.value = false;
      }
    };

    const actionItems = computed<SpmTableAction[]>(() => [
      {
        icon: 'far fa-eye',
        label: t('campaigns.bulk.list.actions.preview'),
        show: (campaign: any) =>
          [
            CampaignsBulkStatusEnum.SENT,
            CampaignsBulkStatusEnum.SENDING,
            CampaignsBulkStatusEnum.SUSPENDED,
            CampaignsBulkStatusEnum.SUSPENDED_BY_USER,
            CampaignsBulkStatusEnum.ARCHIVED,
          ].includes(campaign.status as CampaignsBulkStatusEnum) && findPermission('bulk_campaigns.view'),
        callback: openCampaignPreview,
      },
      {
        icon: 'far fa-edit',
        label: t('campaigns.bulk.list.actions.edit'),
        show: (campaign: any) =>
          [
            CampaignsBulkStatusEnum.DRAFT,
            CampaignsBulkStatusEnum.BEING_EDITED,
            CampaignsBulkStatusEnum.SCHEDULED,
          ].includes(campaign.status as CampaignsBulkStatusEnum) && findPermission('bulk_campaigns.edit'),
        callback: openCampaignEditor,
      },
      {
        icon: 'far fa-clone',
        label: t('campaigns.bulk.list.actions.duplicate.title'),
        show: () => findPermission('bulk_campaigns.duplicate'),
        callback: duplicateCampaign,
      },
    ]);

    const columns = computed<SpmTableColumns[]>(() => [
      {
        field: 'name',
        header: t('campaigns.bulk.list.columns.name'),
        sortable: true,
        filterable: true,
        editable: false,
        style: 'min-width: 240px',
        type: 'text',
        controls: [
          {
            key: 'bulk-date-send',
            field: 'date_send',
            label: t('campaigns.common.columns.sentAt'),
            sortable: true,
            filterable: true,
            type: 'datetime',
            filterSettings: { type: 'date', hideFilterMenu: true },
          },
          {
            key: 'bulk-date-creation',
            field: 'date_creation',
            label: t('campaigns.common.columns.createdAt'),
            sortable: true,
            filterable: true,
            type: 'datetime',
            filterSettings: { type: 'date', hideFilterMenu: true },
          },
          {
            key: 'bulk-date-modification',
            field: 'date_modification',
            label: t('campaigns.common.columns.lastModified'),
            sortable: true,
            filterable: true,
            type: 'datetime',
            filterSettings: { type: 'date', hideFilterMenu: true },
          },
        ],
      },
      {
        field: 'status',
        header: t('campaigns.bulk.list.columns.status'),
        sortable: true,
        filterable: true,
        editable: false,
        style: 'min-width: 140px',
        type: 'text',
        filterSettings: {
          type: 'dropdown',
          options: statusOptions.value,
          defaultMatchMode: OperatorType.Equals,
        },
      },
      {
        field: 'engagement_summary',
        header: t('campaigns.common.headers.performance'),
        sortable: false,
        filterable: false,
        editable: false,
        style: 'min-width: 260px',
        type: 'text',
        custom: true,
        controls: [
          {
            key: 'bulk-engagement-open-rate',
            field: 'open_rate',
            label: t('campaigns.common.metrics.openRate'),
            sortable: true,
          },
          {
            key: 'bulk-engagement-click-rate',
            field: 'click_rate',
            label: t('campaigns.common.metrics.clickRate'),
            sortable: true,
          },
          {
            key: 'bulk-engagement-deliveries',
            field: 'deliveries',
            label: t('campaigns.common.metrics.deliveries'),
            sortable: true,
          },
          {
            key: 'bulk-engagement-opens',
            field: 'opens',
            label: t('campaigns.common.metrics.opens'),
            sortable: true,
          },
          {
            key: 'bulk-engagement-open-unique',
            field: 'open_unique',
            label: t('campaigns.analytics.sendStatistics.items.labels.open_unique'),
            sortable: true,
          },
          {
            key: 'bulk-engagement-clicks',
            field: 'clicks',
            label: t('campaigns.common.metrics.clicks'),
            sortable: true,
          },
          {
            key: 'bulk-engagement-click-unique',
            field: 'click_unique',
            label: t('campaigns.analytics.sendStatistics.items.labels.click_unique'),
            sortable: true,
          },
        ],
      },
      {
        field: 'conversion_summary',
        header: t('campaigns.common.headers.conversions'),
        sortable: false,
        filterable: false,
        editable: false,
        style: 'min-width: 240px',
        type: 'text',
        custom: true,
        controls: [
          {
            key: 'bulk-conversion-sales-amount',
            field: 'sales_amount',
            label: t('campaigns.common.metrics.salesAmount'),
            sortable: true,
          },
          {
            key: 'bulk-conversion-rate',
            field: 'conversion_rate',
            label: t('campaigns.common.metrics.conversionRate'),
            sortable: true,
          },
          {
            key: 'bulk-conversion-sales',
            field: 'sales',
            label: t('campaigns.common.metrics.sales'),
            sortable: true,
          },
          {
            key: 'bulk-conversion-bounces',
            field: 'bounces',
            label: t('campaigns.common.metrics.bounces'),
            sortable: true,
          },
          {
            key: 'bulk-conversion-spam',
            field: 'spam',
            label: t('campaigns.common.metrics.spam'),
            sortable: true,
          },
          {
            key: 'bulk-conversion-unsubscribes',
            field: 'unsubscribes',
            label: t('campaigns.common.metrics.unsubscribes'),
            sortable: true,
          },
        ],
      },
      {
        field: 'actions',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'width: 4rem',
        type: 'action',
        custom: true,
        actions: actionItems.value,
      },
      {
        field: 'date_creation',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'datetime',
        hidden: true,
      },
      {
        field: 'date_send',
        header: '',
        sortable: true,
        filterable: true,
        editable: false,
        style: 'display:none',
        type: 'datetime',
        hidden: true,
      },
      {
        field: 'deliveries',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'opens',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'clicks',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'sales',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'sales_amount',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'open_rate',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'click_rate',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'conversion_rate',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'revenue_per_send',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'primary_channel',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'channels',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'unsubscribes',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'bounces',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'spam',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
    ]);

    const initialSorting = ref<SpmTableSort[]>([
      { field: 'date_creation', type: 'desc' },
    ]);

    const showImportModal = () => {
      isImportModalVisible.value = true;
    };

    const hideImportModal = (needReload: boolean) => {
      isImportModalVisible.value = false;
      if (needReload) {
        setRefreshScenariosList(true);
      }
    };

    const handleCreateCampaign = () => {
      openWorkflowSelector.value = true;
    };

    const reloadTable = () => {
      if (tableRef.value) {
        tableRef.value.reloadData();
      }
    };

    watch(
      () => stateAutomatedScenario.refreshScenariosList,
      (shouldRefresh) => {
        if (shouldRefresh) {
          reloadTable();
          setRefreshScenariosList(false);
        }
      },
    );

    onMounted(() => {
      setRefreshScenariosList(false);
    });

    const formatPercentage = (value: number): string => {
      if (!Number.isFinite(value)) {
        return t('campaigns.common.placeholders.noData');
      }

      const ratio = value / 100;
      return percentageFormatter.format(ratio);
    };

    const formatInteger = (value: number | null | undefined): string => {
      if (value === null || value === undefined) {
        return t('campaigns.common.placeholders.noData');
      }
      return integerFormatter.format(value);
    };

    const formatCombinedMetric = (
      primary: number | null | undefined,
      secondary: number | null | undefined,
    ): string => {
      const isPrimaryMissing = primary === null || primary === undefined;
      const isSecondaryMissing = secondary === null || secondary === undefined;

      if (isPrimaryMissing && isSecondaryMissing) {
        return t('campaigns.common.placeholders.noData');
      }

      const placeholder = t('campaigns.common.placeholders.noData');
      const primaryText = isPrimaryMissing
        ? placeholder
        : integerFormatter.format(primary as number);
      const secondaryText = isSecondaryMissing
        ? placeholder
        : integerFormatter.format(secondary as number);

      return `${primaryText} / ${secondaryText}`;
    };

    const formatAmount = (value: number | null | undefined): string => {
      if (value === null || value === undefined) {
        return t('campaigns.common.placeholders.noData');
      }
      return amountFormatter.format(value);
    };

    const formatChannel = (value: string | null): string => {
      if (!value) {
        return t('campaigns.common.headers.channelUnknown');
      }
      const key = value.toLowerCase();
      const translationKey = `campaigns.common.channels.${key}`;
      const translated = t(translationKey);
      return translated === translationKey ? key : translated;
    };

    const formatDateTime = (value: string | Date | null): string => {
      if (!value) {
        return t('campaigns.common.placeholders.noDate');
      }

      const date = typeof value === 'string' ? new Date(value) : value;
      if (!date || Number.isNaN(date.getTime())) {
        return t('campaigns.common.placeholders.noDate');
      }

      return new Intl.DateTimeFormat(locale.value, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    };

    const extractChannelTags = (row: any): string[] => {
      const uniqueChannels = new Set<string>();

      if (Array.isArray(row?.channels)) {
        row.channels.forEach((channel: any) => {
          if (channel?.channel) {
            uniqueChannels.add(channel.channel);
          }
        });
      }

      if (row?.primary_channel) {
        uniqueChannels.add(row.primary_channel);
      }

      return Array.from(uniqueChannels);
    };

    const buildEngagementMetrics = (row: any): CampaignMetricDisplay[] => [
      {
        id: 'open_rate',
        label: t('campaigns.common.metrics.openRate'),
        value: formatPercentage(row.open_rate),
      },
      {
        id: 'click_rate',
        label: t('campaigns.common.metrics.clickRate'),
        value: formatPercentage(row.click_rate),
      },
    ];

    const buildConversionMetrics = (row: any): CampaignMetricDisplay[] => [
      {
        id: 'sales_amount',
        label: t('campaigns.common.metrics.salesAmount'),
        value: formatAmount(row.sales_amount),
      },
      {
        id: 'conversion_rate',
        label: t('campaigns.common.metrics.conversionRate'),
        value: formatPercentage(row.conversion_rate),
      },
    ];

    const buildExpandedMetrics = (row: any): CampaignMetricDisplay[] => [
      {
        id: 'deliveries',
        label: t('campaigns.common.metrics.deliveries'),
        value: formatInteger(row.deliveries),
      },
      {
        id: 'opens',
        label: t('campaigns.common.metrics.opensWithUnique'),
        value: formatCombinedMetric(row.opens, row.open_unique),
      },
      {
        id: 'clicks',
        label: t('campaigns.common.metrics.clicksWithUnique'),
        value: formatCombinedMetric(row.clicks, row.click_unique),
      },
      {
        id: 'sales',
        label: t('campaigns.common.metrics.sales'),
        value: formatInteger(row.sales),
      },
      {
        id: 'bounces',
        label: t('campaigns.common.metrics.bounces'),
        value: formatInteger(row.bounces),
      },
      {
        id: 'spam',
        label: t('campaigns.common.metrics.spam'),
        value: formatInteger(row.spam),
      },
      {
        id: 'unsubscribes',
        label: t('campaigns.common.metrics.unsubscribes'),
        value: formatInteger(row.unsubscribes),
      },
    ];

    const buildChannelMetrics = (channel: any): CampaignMetricDisplay[] => [
      {
        id: 'deliveries',
        label: t('campaigns.common.metrics.deliveries'),
        value: formatInteger(channel.deliveries),
      },
      {
        id: 'open_rate',
        label: t('campaigns.common.metrics.openRate'),
        value: formatPercentage(channel.open_rate),
      },
      {
        id: 'click_rate',
        label: t('campaigns.common.metrics.clickRate'),
        value: formatPercentage(channel.click_rate),
      },
      {
        id: 'sales_amount',
        label: t('campaigns.common.metrics.salesAmount'),
        value: formatAmount(channel.sales_amount),
      },
      {
        id: 'revenue_per_send',
        label: t('campaigns.common.metrics.revenuePerSend'),
        value: formatAmount(channel.revenue_per_send),
      },
    ];

    const statusStyle = (status: string | null | undefined) => getStatusStyleVars(status);
    const statusIcon = (status: string | null | undefined) => getStatusIcon(status);
    const channelTagStyle = (channel: string | null | undefined) => getChannelTagStyleVars(channel);
    const channelIcon = (channel: string | null | undefined) => getChannelIcon(channel);
    const channelCardStyle = (channel: string | null | undefined) => getChannelCardStyleVars(channel);

    return {
      t,
      idShop,
      tableRef,
      columns,
      initialSorting,
      persistentFilters,
      openWorkflowSelector,
      showImportModal,
      hideImportModal,
      isImportModalVisible,
      handleCreateCampaign,
      formatPercentage,
      formatInteger,
      formatAmount,
      formatChannel,
      formatDateTime,
      extractChannelTags,
      buildEngagementMetrics,
      buildConversionMetrics,
      buildExpandedMetrics,
      buildChannelMetrics,
      statusStyle,
      statusIcon,
      channelTagStyle,
      channelIcon,
      channelCardStyle,
      findPermission,
      groupActions,
    };
  },
});
